import { useState, useEffect, useRef, useCallback } from "react";
import { checkOrchestratorHealth } from "../utils/uploadworker/orchestratorClient";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Polls /health on mount and every POLL_INTERVAL_MS thereafter.
 *
 * Returns:
 *   { status: "loading" | "ok" | "degraded" | "unreachable",
 *     configWarnings: string[],
 *     refresh: () => void }
 *
 * "degraded"   — orchestrator is up but /health returned 503: one or more
 *                critical env values are missing/placeholder; configWarnings
 *                lists each problem.
 * "unreachable" — fetch failed (network error, timeout, server down).
 * "ok"         — /health returned 200, config is clean.
 */
export function useOrchestratorHealth() {
  const [status, setStatus] = useState("loading");
  const [configWarnings, setConfigWarnings] = useState([]);
  const timerRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const result = await checkOrchestratorHealth();
      if (result.degraded) {
        setStatus("degraded");
        setConfigWarnings(result.configWarnings ?? []);
      } else if (result.available) {
        setStatus("ok");
        setConfigWarnings([]);
      } else {
        setStatus("unreachable");
        setConfigWarnings([]);
      }
    } catch {
      setStatus("unreachable");
      setConfigWarnings([]);
    }
  }, []);

  useEffect(() => {
    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [poll]);

  return { status, configWarnings, refresh: poll };
}
